import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { CancelToken, isCancel } from 'axios';

import DM1Service from '../../Services/DM1Service';
import DM1FaultsConstants from '../../Lib/DM1FaultsConstants';

import CurrentOrgContext from '../../Context/CurrentOrgContext';

import DmAlertsFiltersDropdown from './DmAlertsFiltersDropdown';
import SearchByNameDropdown from './SearchByNameDropdown';
import SearchByOrgDropdown from './SearchByOrgDropdown';
import SortByDateButton from './SortByDateButton';
import StatusSelectDropdown from './StatusSelectDropdown';

const { listSourceAddress } = new DM1FaultsConstants();
const initState = { SPN: [], FMI: [], Source: [] };
const FILTER_HEADERS = [
  { displayText: 'SPN', filter: 'spn' },
  { displayText: 'FMI', filter: 'fmi' },
  { displayText: 'Source', filter: 'sa' },
  { displayText: 'CAN', filter: 'canbus' },
];

function DmAlertsTableHeader(props) {
  const {
    asset: { _id: gatewayIdFilter, orgID: assetOrgID } = {},
    assetView,
    engineAppView,
    handleAssetSelected,
    handleDmFilterSelected,
    handleEcosystemToggle,
    handleOrgSelected,
    handleSortClick,
    handleStatusSelected,
    includeChildren,
    orgIdFilter,
    selectedStatus,
    setError,
    sort,
  } = props;
  const currentOrg = useContext(CurrentOrgContext) || {};

  const orgID = useMemo(
      () => (assetView ? assetOrgID : orgIdFilter || currentOrg?._id),
      [assetView, assetOrgID, currentOrg?._id, orgIdFilter]
    ),
    source = useMemo(() => CancelToken.source(), []),
    service = useMemo(() => new DM1Service(), []),
    metricsRequestParams = useMemo(() => {
      const conditionalParam = gatewayIdFilter ? { gatewayIdFilter } : {};
      return { orgID, orgIdFilter: orgID, ...conditionalParam };
    }, [gatewayIdFilter, orgID]);

  const [filters, setFilters] = useState(initState);

  const formatOptions = useCallback(
    (option) => ({ value: option, label: option }),
    []
  );

  const formatSourceOptions = useCallback(
    (value) => ({ value, label: listSourceAddress[value] || 'Unknown SA' }),
    []
  );

  const fetchDm1Filters = useCallback(async () => {
    try {
      const {
        Canbus: canbus = [],
        SourceAddress: sa = [],
        FMI: fmi = [],
        SPN: spn = [],
      } = await service.getDM1Filters(metricsRequestParams, source.token);

      const filters = {
        spn: Array.from(spn)
          .sort((a, b) => a - b)
          .map(formatOptions),
        fmi: Array.from(fmi)
          .sort((a, b) => a - b)
          .map(formatOptions),
        sa: Array.from(sa)
          .sort((a, b) => a - b)
          .map(formatSourceOptions),
        canbus: Array.from(canbus)
          .sort((a, b) => a - b)
          .map(formatOptions),
      };
      setFilters(filters);
    } catch (err) {
      if (isCancel(err)) return;
      const error = err.response?.data?.message ?? err.message;
      setError(error);
    }
  }, [
    formatOptions,
    formatSourceOptions,
    metricsRequestParams,
    service,
    setError,
    source.token,
  ]);

  useEffect(() => {
    switch (true) {
      case assetView:
        if (!assetOrgID) return;
        fetchDm1Filters();
        break;
      default:
        if (!currentOrg?._id) return;
        fetchDm1Filters();
        break;
    }
  }, [assetView, currentOrg?._id, assetOrgID, fetchDm1Filters]);

  return (
    <tr>
      <th scope="col" className="sticky-top bg-white border-top-0">
        <StatusSelectDropdown
          selectedStatus={selectedStatus}
          handleStatusSelected={handleStatusSelected}
        />
      </th>
      {assetView || engineAppView ? null : (
        <th
          scope="col"
          className="text-text-light-gray font-weight-normal three-quarter-text px-0 pb-1 sticky-top bg-white border-top-0"
        >
          <SearchByNameDropdown
            orgID={currentOrg._id}
            resource="Asset"
            handleAssetSelected={handleAssetSelected}
          />
        </th>
      )}
      <th
        scope="col"
        className="text-text-light-gray font-weight-normal three-quarter-text sticky-top bg-white border-top-0"
      >
        Name
      </th>
      {assetView ? null : (
        <th
          scope="col"
          className="text-text-light-gray font-weight-normal three-quarter-text px-0 pb-1 w-25 sticky-top bg-white border-top-0"
        >
          <SearchByOrgDropdown
            handleEcosystemToggle={handleEcosystemToggle}
            handleOrgSelected={handleOrgSelected}
            includeChildren={includeChildren}
            orgID={orgID}
            orgIdFilter={orgIdFilter}
          />
        </th>
      )}
      <th
        scope="col"
        className="text-text-light-gray font-weight-normal three-quarter-text text-center sticky-top bg-white border-top-0"
      >
        LAMP
      </th>
      <th
        scope="col"
        className="col-date text-text-light-gray font-weight-normal three-quarter-text sticky-top bg-white border-top-0"
      >
        <SortByDateButton
          displayText="Detected At"
          sort={sort}
          handleSortClick={handleSortClick}
        />
      </th>
      {FILTER_HEADERS.map(({ displayText, filter }) => {
        const filterRef = {
          [`${filter}FilterRef`]: props[`${filter}FilterRef`],
        };
        return (
          <th
            scope="col"
            key={filter}
            className="text-text-light-gray font-weight-normal three-quarter-text px-0 pb-0 sticky-top bg-white border-top-0"
          >
            <DmAlertsFiltersDropdown
              displayText={displayText}
              filter={filter}
              options={filters[filter] || []}
              handleOptionSelect={handleDmFilterSelected}
              {...filterRef}
            />
          </th>
        );
      })}
      <th
        scope="col"
        className={`text-text-light-gray font-weight-normal ${
          engineAppView ? 'text-center ' : ''
        }three-quarter-text sticky-top bg-white border-top-0`}
      >
        Occurrences
      </th>
      {engineAppView ? (
        <th
          scope="col"
          className="text-text-light-gray font-weight-normal three-quarter-text text-center sticky-top bg-white border-top-0"
        >
          Snapshots
        </th>
      ) : null}
    </tr>
  );
}

DmAlertsTableHeader.propTypes = {
  asset: PropTypes.shape({
    _id: PropTypes.string,
    orgID: PropTypes.string,
  }),
  assetView: PropTypes.bool,
  engineAppView: PropTypes.bool,
  fmiFilterRef: PropTypes.object,
  handleAssetSelected: PropTypes.func,
  handleDmFilterSelected: PropTypes.func,
  handleEcosystemToggle: PropTypes.func,
  handleOrgSelected: PropTypes.func,
  handleSortClick: PropTypes.func,
  handleStatusSelected: PropTypes.func,
  includeChildren: PropTypes.bool,
  orgIdFilter: PropTypes.string,
  saFilterRef: PropTypes.object,
  selectedStatus: PropTypes.oneOf(['positive', 'negative', 'all']),
  setDmExport: PropTypes.func,
  setError: PropTypes.func,
  sort: PropTypes.shape({
    by: PropTypes.oneOf(['TriggeredAt', 'DetectedAt']),
    direction: PropTypes.oneOf(['ASC', 'DESC']),
  }),
  spnFilterRef: PropTypes.object,
  tab: PropTypes.oneOf(['alerts', 'dm1']),
};

DmAlertsTableHeader.defaultProps = { data: [], asset: {} };

export default memo(DmAlertsTableHeader);
